import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { merge, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { AisleModel } from "src/app/_old-sublocations/moved-models/aisle.model";
import { BinModel } from "src/app/_old-sublocations/moved-models/bin.model";
import { ShelfModel } from "src/app/_old-sublocations/moved-models/shelf.model";
import { UnitModel } from "src/app/_old-sublocations/moved-models/unit.model";
import { LocationNode } from "src/app/_old-sublocations/moved-pages-sublocations/location-node";

@Component({
    selector: 'assign-sublocation',
    templateUrl: './assign-sublocation.component.html',
    styleUrls: ['./assign-sublocation.component.scss'],
})
export class AssignSublocationComponent implements OnInit {
    @Input() aisles: AisleModel[];
    selectedAisle: AisleModel;
    selectedUnit: UnitModel;
    selectedShelf: ShelfModel;
    @Input() selectedBin: BinModel;
    @Output() selectBin: EventEmitter<BinModel> = new EventEmitter();
    @Output() validity: EventEmitter<boolean> = new EventEmitter();
    formGroup: UntypedFormGroup;

    constructor(private fb: UntypedFormBuilder) { }

    ngOnInit() {
        this.formGroup = this.fb.group({
            aisle: [null],
            unit: [{ value: null, disabled: true }],
            shelf: [{ value: null, disabled: true }],
            bin: [{ value: null, disabled: true }]
        });

        this.formGroup.controls.aisle.valueChanges.subscribe(e => this.aisleSelected(e));
        this.formGroup.controls.unit.valueChanges.subscribe(e => this.unitSelected(e));
        this.formGroup.controls.shelf.valueChanges.subscribe(e => this.shelfSelected(e));
        this.formGroup.controls.bin.valueChanges.subscribe(e => this.binSelected(e));
    }

    getSubLocName(subLoc: LocationNode) {
        return subLoc.name || `${subLoc.isLeft === undefined ? '' : (subLoc.isLeft ? 'L' : 'R')}${subLoc.index < 10 && subLoc.isLeft === undefined ? '0' : ''}${subLoc.index}`;
    }

    aisleSelected(aisle: AisleModel) {
        if (this.selectedAisle != aisle) {
            this.selectedAisle = aisle;
            this.unitSelected(undefined);
            this.formGroup.controls.unit.setValue(undefined);
            if (this.selectedAisle) {
                this.formGroup.controls.unit.addValidators(Validators.required);
                this.formGroup.controls.unit.enable();
                this.validity.emit(false);
            } else {
                this.formGroup.controls.unit.removeValidators(Validators.required);
                this.formGroup.controls.unit.disable();
                this.validity.emit(true);
            }
            this.formGroup.controls.unit.updateValueAndValidity();
        }
    }

    unitSelected(unit: UnitModel) {
        if (this.selectedUnit != unit) {
            this.selectedUnit = unit;
            this.shelfSelected(undefined);
            this.formGroup.controls.shelf.setValue(undefined);
            if (this.selectedUnit) {
                this.formGroup.controls.shelf.addValidators(Validators.required);
                this.formGroup.controls.shelf.enable();
            } else {
                this.formGroup.controls.shelf.removeValidators(Validators.required);
                this.formGroup.controls.shelf.disable();
            }
            this.formGroup.controls.shelf.updateValueAndValidity();
        }
    }

    shelfSelected(shelf: ShelfModel) {
        if (this.selectedShelf != shelf) {
            this.selectedShelf = shelf;
            this.binSelected(undefined);
            this.formGroup.controls.bin.setValue(undefined);
            if (this.selectedShelf) {
                this.formGroup.controls.bin.addValidators(Validators.required);
                this.formGroup.controls.bin.enable();
            } else {
                this.formGroup.controls.bin.removeValidators(Validators.required);
                this.formGroup.controls.bin.disable();
            }
            this.formGroup.controls.bin.updateValueAndValidity();
        }
    }

    binSelected(bin: BinModel) {
        if (this.selectedBin != bin) {
            this.selectedBin = bin;
            this.selectBin.emit(this.selectedBin);
            this.validity.emit(!!this.selectedBin);
        }
    }

    subLocFormatter = (subLoc) => this.getSubLocName(subLoc);

    focus$ = [new Subject<string>(), new Subject<string>(), new Subject<string>(), new Subject<string>()];

    aisleSearch = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
        const inputFocus$ = this.focus$[0];
        return merge(debouncedText$, inputFocus$).pipe(
            map((term) => (term === '' ? this.aisles : this.aisles.filter(a => this.getSubLocName(a).includes(term))))
        );
    };

    unitSearch = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
        const inputFocus$ = this.focus$[1];
        return merge(debouncedText$, inputFocus$).pipe(
            map(term => (term === '' ? this.selectedAisle?.units : this.selectedAisle?.units
                .filter(a => this.getSubLocName(a).includes(term.toUpperCase()))))
        );
    };

    shelfSearch = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
        const inputFocus$ = this.focus$[2];
        return merge(debouncedText$, inputFocus$).pipe(
            map(term => (term === '' ? this.selectedUnit?.shelves : this.selectedUnit?.shelves
                .filter(a => this.getSubLocName(a).includes(term))))
        );
    };

    binSearch = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
        const inputFocus$ = this.focus$[3];
        return merge(debouncedText$, inputFocus$).pipe(
            map(term => (term === '' ? this.selectedShelf?.bins : this.selectedShelf?.bins
                .filter(a => this.getSubLocName(a).includes(term))))
        );
    };

    emitValue(event, index: number) {
        this.focus$[index].next(event.target.value);
    }
}